export interface GeoIPResult {
  ip: string;
  lat: number;
  lng: number;
  city: string;
  country: string;
  countryCode: string;
}

const cache = new Map<string, GeoIPResult>();
const STORAGE_KEY = 'rehub-geoip-v1';
let persistLoaded = false;

function loadPersisted() {
  if (persistLoaded || typeof localStorage === 'undefined') return;
  persistLoaded = true;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    const obj = JSON.parse(raw) as Record<string, GeoIPResult>;
    Object.values(obj).forEach((v) => {
      if (v?.ip && typeof v.lat === 'number' && typeof v.lng === 'number') {
        cache.set(v.ip, v);
      }
    });
  } catch {
    /* ignore corrupt cache */
  }
}

function persistCache() {
  if (typeof localStorage === 'undefined') return;
  try {
    const obj: Record<string, GeoIPResult> = {};
    cache.forEach((v, k) => {
      obj[k] = v;
    });
    localStorage.setItem(STORAGE_KEY, JSON.stringify(obj));
  } catch {
    /* quota / private mode */
  }
}

export function isPublicIP(ip: string | null | undefined): ip is string {
  if (!ip) return false;
  const v = ip.trim().toLowerCase();
  if (!v || v === 'unknown' || v === '::1' || v === 'localhost') return false;
  if (v.startsWith('127.') || v.startsWith('10.') || v.startsWith('192.168.') || v.startsWith('0.')) return false;
  if (/^172\.(1[6-9]|2\d|3[0-1])\./.test(v)) return false;
  if (v.startsWith('fe80:') || v.startsWith('fc') || v.startsWith('fd')) return false;
  return true;
}

async function lookupSingle(ip: string): Promise<GeoIPResult | null> {
  if (cache.has(ip)) return cache.get(ip)!;

  try {
    const res = await fetch(`https://ipwho.is/${ip}`);
    const data = await res.json();
    if (!data.success) return null;

    const result: GeoIPResult = {
      ip,
      lat: data.latitude,
      lng: data.longitude,
      city: data.city || 'Unknown',
      country: data.country || 'Unknown',
      countryCode: data.country_code || '',
    };
    cache.set(ip, result);
    return result;
  } catch {
    return null;
  }
}

/**
 * Resolve an array of IPs to geo coordinates.
 * Uses ipwho.is (free, CORS-friendly, no key needed).
 * Batches in groups of 10 with small delays to respect rate limits.
 */
export async function resolveIPs(
  ips: string[],
  onBatch?: (results: Map<string, GeoIPResult>) => void
): Promise<Map<string, GeoIPResult>> {
  loadPersisted();
  const results = new Map<string, GeoIPResult>();
  const unique = Array.from(new Set(ips.filter(isPublicIP)));
  const uncached: string[] = [];

  unique.forEach((ip) => {
    const hit = cache.get(ip);
    if (hit) results.set(ip, hit);
    else uncached.push(ip);
  });

  if (results.size) onBatch?.(new Map(results));

  const batchSize = 10;
  for (let i = 0; i < uncached.length; i += batchSize) {
    const batch = uncached.slice(i, i + batchSize);
    const settled = await Promise.allSettled(batch.map(lookupSingle));
    settled.forEach((s, idx) => {
      if (s.status === 'fulfilled' && s.value) {
        results.set(batch[idx], s.value);
      }
    });
    persistCache();
    onBatch?.(new Map(results));
    if (i + batchSize < uncached.length) {
      await new Promise((r) => setTimeout(r, 200));
    }
  }

  return results;
}
